/**
 * 全局日志管理器
 * 统一管理所有组件的日志输出，支持不同级别的日志控制
 * 
 * @class LogManager
 * @version 1.0.0
 */
class LogManager {
  static instance = null;
  
  constructor() {
    this.config = {
      // 日志级别: 0=debug, 1=info, 2=warn, 3=error, 4=critical, 5=off
      globalLevel: process.env.NODE_ENV === 'development' ? 1 : 3,
      
      // 组件级别配置（可以覆盖全局配置）
      componentLevels: {
        'ChatItem': 4,           // 只显示critical级别以上（基本不显示）
        'InboxSession': 2,       // 只显示warn级别以上
        'EditorChat': 1,         // 显示info级别以上
        'VueReactBridge': 2,     // 只显示warn级别以上
        'AIChatSimplified': 2,   // 只显示warn级别以上
      },
      
      // 关键事件总是显示（即使组件日志级别很高）
      criticalEvents: [
        'AI会话同步完成',
        'Bridge初始化完成',
        '加载会话数据失败',
        '达到最大重试次数',
        '严重错误',
        '致命错误',
        'Bridge未初始化'
      ]
    };
    
    this.levels = {
      debug: 0,
      info: 1, 
      warn: 2,
      error: 3,
      critical: 4,
      off: 5
    };
  }
  
  static getInstance() {
    if (!LogManager.instance) {
      LogManager.instance = new LogManager();
    }
    return LogManager.instance;
  }
  
  /**
   * 检查是否应该输出日志
   */
  shouldLog(component, level, message) {
    const numLevel = typeof level === 'string' ? this.levels[level] : level;
    
    // 检查是否是关键事件
    const isCritical = this.config.criticalEvents.some(event => 
      message.includes(event)
    );
    
    if (isCritical) {
      return true; // 关键事件总是显示
    }
    
    // 获取组件级别（如果没有设置则使用全局级别）
    const componentLevel = this.config.componentLevels[component] ?? this.config.globalLevel;
    
    return numLevel >= componentLevel;
  }
  
  /**
   * 输出debug级别日志
   */
  debug(component, ...args) {
    if (this.shouldLog(component, 'debug', args.join(' '))) {
      console.log(`[${component}:DEBUG]`, ...args);
    }
  }
  
  /**
   * 输出info级别日志
   */
  info(component, ...args) {
    if (this.shouldLog(component, 'info', args.join(' '))) {
      console.log(`[${component}:INFO]`, ...args);
    }
  }
  
  /**
   * 输出warn级别日志
   */
  warn(component, ...args) {
    if (this.shouldLog(component, 'warn', args.join(' '))) {
      console.warn(`[${component}:WARN]`, ...args);
    }
  }
  
  /**
   * 输出error级别日志
   */
  error(component, ...args) {
    if (this.shouldLog(component, 'error', args.join(' '))) {
      console.error(`[${component}:ERROR]`, ...args);
    }
  }
  
  /**
   * 输出critical级别日志（总是显示）
   */
  critical(component, ...args) {
    console.error(`[${component}:CRITICAL]`, ...args);
  }
  
  /**
   * 设置组件日志级别
   */
  setComponentLevel(component, level) {
    const numLevel = typeof level === 'string' ? this.levels[level] : level;
    this.config.componentLevels[component] = numLevel;
  }
  
  /**
   * 设置全局日志级别
   */
  setGlobalLevel(level) {
    const numLevel = typeof level === 'string' ? this.levels[level] : level;
    this.config.globalLevel = numLevel;
  }
  
  /**
   * 获取当前配置
   */
  getConfig() {
    return { ...this.config };
  }
}

// 创建全局实例
const logManager = LogManager.getInstance();

// 导出便捷方法
export const log = {
  debug: (component, ...args) => logManager.debug(component, ...args),
  info: (component, ...args) => logManager.info(component, ...args),
  warn: (component, ...args) => logManager.warn(component, ...args),
  error: (component, ...args) => logManager.error(component, ...args),
  critical: (component, ...args) => logManager.critical(component, ...args),
  
  setLevel: (component, level) => logManager.setComponentLevel(component, level),
  setGlobalLevel: (level) => logManager.setGlobalLevel(level),
  getConfig: () => logManager.getConfig()
};

export default logManager; 